import { DATA_BASE_URL } from '@lib/constants';
import Icon from '@components/common/Icon';

const CertificatesSection = () => {
  const certificates = [
    {
      id: 'albertson',
      company: 'Albertsons Companies Inc.',
      title: 'iPad App Developer Internship',
      period: 'June 2024',
      location: 'Philippines',
      pdf: `${DATA_BASE_URL}/data/internships/albertson/albertson_internship_2024.pdf`,
      description: 'Certificate of completion for successful enhancement of retail iPad application',
      icon: 'cart',
      color: 'var(--color-accent-5)',
    },
    {
      id: 'omind',
      company: 'Omind',
      title: 'AI Agent Development Internship',
      period: 'June - July 2025',
      location: 'USA',
      pdf: `${DATA_BASE_URL}/data/internships/omind/omind_internship_certificate_2025.pdf`,
      description: 'Certificate recognizing contributions to AI support agent development',
      icon: 'robot',
      color: 'var(--color-accent-6)',
    },
    {
      id: 'philtech',
      company: 'SRP Technologies (Honda Manufacturing)',
      title: 'Tech Internship - Robotics & Automation',
      period: '2024',
      location: 'Manesar, Haryana, India',
      pdf: `${DATA_BASE_URL}/data/internships/philtech/philtech_internship_2024.pdf`,
      description: 'Certificate of participation in industrial robotics and automation program',
      icon: 'robotics',
      color: 'var(--color-accent-7)',
    },
  ];

  return (
    <section
      className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8"
      style={{ backgroundColor: 'var(--color-theme-2)' }}
    >
      <div className="container mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="text-center mb-12 space-y-4">
          <h2
            className="text-4xl sm:text-5xl font-heading font-bold"
            style={{ color: 'var(--color-heading)' }}
          >
            Certificates
          </h2>
          <p
            className="text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Official recognition from leading organizations
          </p>
        </div>

        {/* Certificates Grid - Horizontal scroll on mobile, grid on desktop */}
        <div>
          {/* Mobile: Horizontal scroll */}
          <div className="md:hidden flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
            {certificates.map((cert) => (
              <div
                key={cert.id}
                className="group rounded-xl overflow-hidden transition-all duration-300 min-w-[300px] flex-shrink-0 snap-center"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
              {/* PDF Preview Area - Clickable */}
              <a
                href={cert.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="relative h-64 flex items-center justify-center overflow-hidden cursor-pointer block"
                style={{
                  backgroundColor: 'var(--color-theme-3)',
                  borderBottomColor: 'var(--color-border-light)',
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid'
                }}
              >
                {/* PDF Icon Overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border-light)',
                      borderWidth: '2px',
                      borderStyle: 'solid'
                    }}
                  >
                    <Icon name={cert.icon} size="2xl" className="text-5xl" style={{ color: cert.color }} />
                  </div>
                  <div
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      color: cert.color
                    }}
                  >
                    PDF Certificate
                  </div>
                </div>

                {/* Decorative Pattern */}
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
                  style={{ backgroundColor: cert.color }}
                ></div>
                <div
                  className="absolute bottom-0 left-0 w-32 h-32 rounded-full mix-blend-multiply filter blur-3xl opacity-10"
                  style={{ backgroundColor: cert.color }}
                ></div>
              </a>

              {/* Certificate Info */}
              <div className="p-6 space-y-4">
                {/* Company and Location */}
                <div>
                  <h3
                    className="font-bold text-lg mb-1"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    {cert.company}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {cert.location}
                  </p>
                </div>

                {/* Title and Period */}
                <div>
                  <h4
                    className="font-semibold text-base"
                    style={{ color: 'var(--color-subheading)' }}
                  >
                    {cert.title}
                  </h4>
                  <p
                    className="text-sm mt-1"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {cert.period}
                  </p>
                </div>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--color-subtle)' }}
                >
                  {cert.description}
                </p>

                {/* View Button */}
                <a
                  href={cert.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: 'var(--color-button-primary-bg)',
                    color: 'var(--color-button-primary-text)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-button-primary-hover)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-button-primary-bg)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  View Certificate
                </a>
              </div>
            </div>
          ))}
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {certificates.map((cert) => (
              <div
                key={cert.id}
                className="group rounded-xl overflow-hidden transition-all duration-300 hover:shadow-card-hover"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  borderColor: 'var(--color-card-border)',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}
              >
              {/* PDF Preview Area - Clickable */}
              <a
                href={cert.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="relative h-64 flex items-center justify-center overflow-hidden cursor-pointer block"
                style={{
                  backgroundColor: 'var(--color-theme-3)',
                  borderBottomColor: 'var(--color-border-light)',
                  borderBottomWidth: '1px',
                  borderBottomStyle: 'solid'
                }}
              >
                {/* PDF Icon Overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center space-y-4">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      borderColor: 'var(--color-border-light)',
                      borderWidth: '2px',
                      borderStyle: 'solid'
                    }}
                  >
                    <Icon name={cert.icon} size="2xl" className="text-5xl" style={{ color: cert.color }} />
                  </div>
                  <div
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{
                      backgroundColor: 'var(--color-card-bg)',
                      color: cert.color
                    }}
                  >
                    PDF Certificate
                  </div>
                </div>

                {/* Decorative Pattern */}
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
                  style={{ backgroundColor: cert.color }}
                ></div>
                <div
                  className="absolute bottom-0 left-0 w-32 h-32 rounded-full mix-blend-multiply filter blur-3xl opacity-10"
                  style={{ backgroundColor: cert.color }}
                ></div>
              </a>

              {/* Certificate Info */}
              <div className="p-6 space-y-4">
                {/* Company and Location */}
                <div>
                  <h3
                    className="font-bold text-lg mb-1"
                    style={{ color: 'var(--color-heading)' }}
                  >
                    {cert.company}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {cert.location}
                  </p>
                </div>

                {/* Title and Period */}
                <div>
                  <h4
                    className="font-semibold text-base"
                    style={{ color: 'var(--color-subheading)' }}
                  >
                    {cert.title}
                  </h4>
                  <p
                    className="text-sm mt-1"
                    style={{ color: 'var(--color-muted)' }}
                  >
                    {cert.period}
                  </p>
                </div>

                {/* Description */}
                <p
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--color-subtle)' }}
                >
                  {cert.description}
                </p>

                {/* View Button */}
                <a
                  href={cert.pdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200"
                  style={{
                    backgroundColor: 'var(--color-button-primary-bg)',
                    color: 'var(--color-button-primary-text)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-button-primary-hover)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-button-primary-bg)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  View Certificate
                </a>
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p
            className="text-sm"
            style={{ color: 'var(--color-muted)' }}
          >
            All certificates are official documents issued by respective organizations
          </p>
        </div>
      </div>
    </section>
  );
};

export default CertificatesSection;
